import useUpdateEffect from '@shared/hooks/useUpdateEffect';
import reportError from '@shared/reportError';
import { dateToString } from '@shared/util/date';
import { deepEqual, getIn } from '@shared/util/object';
import Button from '@ui/Button';
import {
  ChangeEvent,
  Children,
  ComponentProps,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type MutableRefObject,
} from 'react';
import { StoreApi, useStore } from 'zustand';
import type { SchemaDescription } from '../yupWithCustomValidations';
import {
  Actions,
  FormProps,
  InitialValues,
  Store,
  ValidatedHandler,
  ValidationMode,
  Value,
  createFormStore,
  validateForm,
} from './store';

type ContextType = {
  store: StoreApi<Store<any>>;
  validationMode?: ValidationMode;
};
const Context = createContext(null as unknown as ContextType);

const getValueBasedOnInputType = (target: ChangeEvent<HTMLInputElement>['target']) => {
  const { value, checked, type, files } = target;

  switch (type) {
    case 'checkbox':
      return checked;
    case 'file':
      return files || '';
    case 'number':
      return value ? Number(value).toString() : '0';
    default:
      return value;
  }
};

const isChangeEvent = (e: any): e is ChangeEvent<HTMLInputElement> => {
  return e && typeof e === 'object' && 'target' in e;
};

/**
 * Returns the most essential props for a form field.
 *
 * @template TValue - The type of the field value
 * @param {string} name - The name/path of the form field
 *
 * @returns An object containing field state and handlers
 *
 * @example
 * const { value, error, onChange, onBlur } = useFormField('user.name');
 *
 * @requires {@link FormProvider} - only works within form contexts
 * @see {@link Form}
 */
export const useFormField = <TValue extends Value = Value>(name: string) => {
  const { store, validationMode } = useContext(Context);
  const value = useStore(store, (state) => getIn(state.values, name)) as TValue;
  const error = useStore(store, (state) => getIn(state.errors, name)) as string;
  const validationSchema = useStore(store, ({ validationSchema }) => validationSchema);
  const setFieldValue = useStore(store, (state) => state.actions.setFieldValue);
  const setFieldTouched = useStore(store, (state) => state.actions.setFieldTouched);

  const required = useMemo(() => {
    try {
      return (
        (validationSchema.describe().fields[name] as SchemaDescription | undefined)?.optional ===
        false
      );
    } catch (e) {
      if (e instanceof Error) {
        reportError(e);
      }
      return false;
    }
  }, [name, validationSchema]);

  const validateField = useCallback(
    () => validateForm({ validationSchema, store, field: name }),
    [name, store, validationSchema],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | TValue | null) => {
      if (isChangeEvent(e)) {
        setFieldValue(name, getValueBasedOnInputType(e.target));
      } else {
        setFieldValue(name, e);
      }
      validationMode === 'onChange' && validateField();
    },
    [name, setFieldValue, validateField, validationMode],
  );

  const onBlur = useCallback(() => {
    setFieldTouched(name);

    validationMode === 'onBlur' && validateField();
  }, [name, setFieldTouched, validateField, validationMode]);

  return {
    value,
    error,
    required,
    onChange,
    onBlur,
    validateField,
    setFieldValue,
  };
};

export const useSetFieldTouched = () => {
  const { store } = useContext(Context);
  return useStore(store, (state) => state.actions.setFieldTouched);
};

export const useIsFieldDirty = (name: string) => {
  const { store } = useContext(Context);
  return useStore(store, ({ values, initialValues }) =>
    typeof values[name] === 'object'
      ? !deepEqual(values[name], initialValues[name])
      : values[name] !== initialValues[name],
  );
};

/**
 * @internal
 */
export const useHasFormContext = () => {
  const store = useContext(Context);
  return !!store;
};

/**
 * @internal
 */
export const useFormActions = () => {
  const { store } = useContext(Context);
  const actions = useStore(store, (state) => state.actions);
  return actions;
};

/**
 * Returns whether the form is currently submitting.
 */
export const useIsSubmitting = () => {
  const { store } = useContext(Context);
  const isSubmitting = useStore(store, (state) => state.isSubmitting);
  return isSubmitting;
};

/**
 * Returns whether the form is dirty (i.e. has been modified).
 * @requires {@link FormProvider} - only works within form contexts
 * @see {@link Form}
 */
export const useIsDirty = () => {
  const { store } = useContext(Context);
  return useStore(store, ({ values, initialValues }) => !deepEqual(values, initialValues));
};

export const useRevalidateFields = (fieldsNames?: string[] | string) => {
  const { store } = useContext(Context);
  const validate = useStore(store, (state) => state.actions.validate);
  const touched = useStore(store, (state) => state.touched);

  return useCallback(async () => {
    if (!fieldsNames?.length) {
      return;
    }
    const fieldsNamesArray = Array.isArray(fieldsNames) ? fieldsNames : [fieldsNames];
    const touchedFields = fieldsNamesArray.filter((fieldName) => touched[fieldName]);
    return await Promise.all(touchedFields.map((fieldName) => validate(fieldName)));
  }, [validate, touched, fieldsNames]);
};

export const useResetFormErrors = () => {
  const { store } = useContext(Context);
  return useStore(store, (state) => state.actions.resetErrorState);
};

export const useValidationBasedControls = (name: string) => {
  const { store } = useContext(Context);
  const validationSchema = useStore(store, (state) => state.validationSchema);
  const fieldValidationSchema = useMemo(() => {
    try {
      const schemaDescription = validationSchema.fields[name]?.describe() as
        | SchemaDescription
        | undefined;
      if (!schemaDescription) {
        return {};
      }
      const descriptionProperties = schemaDescription.tests.reduce((props, { name, params }) => {
        return {
          ...props,
          maxDate: name === 'max' ? dateToString(params?.max as Date) : undefined,
        };
      }, {}) as {
        maxDate?: string;
      };
      return descriptionProperties;
    } catch (e) {
      if (e instanceof Error) {
        reportError(e);
      }
      return {};
    }
  }, [name, validationSchema]);

  return fieldValidationSchema;
};

const ScrollToFormError = () => {
  const isSubmitting = useIsSubmitting();
  const { store } = useContext(Context);
  const errors = useStore(store, (state) => state.errors);

  useUpdateEffect(() => {
    if (isSubmitting) {
      const names = Object.keys(errors);

      if (names.length > 0) {
        const element = names.reduce(
          (found, name) => found ?? document.querySelector<HTMLInputElement>(`[name="${name}"]`),
          null as HTMLInputElement | null,
        );
        element?.focus();
      }
    }
  }, [isSubmitting]);

  return null;
};

export type SubmitFunction<T> = (
  values: T,
  actions: Actions<T> & { validateForm: () => Promise<boolean> },
) => Promise<void | unknown> | void;

export type FormHandle<T> = StoreApi<Store<T>>;

type FormProviderProps<T> = FormProps<T> & {
  formRef?: MutableRefObject<FormHandle<T> | undefined>;
  onValidated?: ValidatedHandler<T>;
  children: ReactNode;
};

export const FormProvider = <T extends InitialValues>(props: FormProviderProps<T>) => {
  const {
    children,
    initialValues,
    onValidated,
    validationSchema,
    onSubmit,
    validationMode,
    formRef,
  } = props;
  const [store] = useState(() =>
    createFormStore({ initialValues, onValidated, validationSchema, onSubmit }),
  );

  useUpdateEffect(() => {
    store.setState({ validationSchema });
  }, [validationSchema]);

  useUpdateEffect(() => {
    store.setState({ onSubmit });
  }, [onSubmit]);

  if (formRef) {
    formRef.current = store;
  }

  return (
    <Context.Provider
      value={{
        store,
        validationMode,
      }}
    >
      {children}
    </Context.Provider>
  );
};

type InternalFormProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  scrollToError?: boolean;
};
export const InternalForm = (props: InternalFormProps) => {
  const { children, scrollToError, ...restProps } = props;
  const { store } = useContext(Context);
  const {
    actions: { submitForm },
  } = store.getState();

  return (
    <form
      {...restProps}
      noValidate={true}
      onSubmit={(e) => {
        e.preventDefault();
        submitForm();
      }}
    >
      {scrollToError && <ScrollToFormError />}
      {children}
    </form>
  );
};

type Props<T> = InternalFormProps & FormProviderProps<T>;

const Form = <T extends InitialValues>(props: Props<T>) => {
  const {
    children,
    initialValues,
    onSubmit,
    onValidated,
    validationSchema,
    validationMode,
    formRef,
    ...restProps
  } = props;

  return (
    <FormProvider
      formRef={formRef}
      initialValues={initialValues}
      onValidated={onValidated}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validationMode={validationMode}
    >
      <InternalForm {...restProps}>{children}</InternalForm>
    </FormProvider>
  );
};

export const SubmitButton = (props: ComponentProps<typeof Button>) => {
  const { store } = useContext(Context);
  const isSubmitting = useStore(store, (state) => state.isSubmitting);

  return <Button {...props} type="submit" disabled={props.disabled || isSubmitting} />;
};

const getFieldName = (nameOrder: string, field?: string) => {
  return field ? `${nameOrder}.${field}` : nameOrder;
};

type RenderParams = {
  getFieldName: (field?: string) => string;
  getFieldIndex: () => number;
  getFieldData: () => Record<string, Value>;
  remove: () => void;
  getFieldError: () => string;
  size: number;
  index: number;
};

type FieldListProps = {
  name: string;
  render: (params: RenderParams) => ReactNode;
  className?: string;
};

export const List = ({ name, render, className }: FieldListProps) => {
  const { store } = useContext(Context);
  const length = useStore(store, (state) => (getIn(state.values, name) as Value[]).length);
  const deleteFromArrayField = useStore(store, (state) => state.actions.deleteFromArrayField);
  const validationSchema = useStore(store, ({ validationSchema }) => validationSchema);
  const errors = useStore(store, (state) => state.errors);

  const remove = useCallback(
    (name: string, index: number) => {
      deleteFromArrayField(name, index);

      const hasValidated = store.getState().errors[name];
      if (hasValidated) {
        validateForm({ validationSchema, store, field: name });
      }
    },
    [deleteFromArrayField, store, validationSchema],
  );

  const validateField = useCallback(
    () => validateForm({ validationSchema, store, field: name }),
    [name, store, validationSchema],
  );

  return (
    <div onBlur={validateField} className={className}>
      {Array.from({ length }).map((_, index) =>
        render({
          getFieldError: () => getIn(errors, `${name}.${index}`) as string,
          getFieldName: (field?: string) => getFieldName(`${name}.${index}`, field),
          getFieldIndex: () => index,
          getFieldData: () => getIn(store.getState().values, `${name}.${index}`)!,
          remove: () => remove(name, index),
          size: length,
          index,
        }),
      )}
    </div>
  );
};

type ListPushProps = {
  name: string;
  children: ReactElement;
  value?: any;
  max?: number;
};
export const ListPush = ({ children, name, value, max }: ListPushProps) => {
  const { store } = useContext(Context);
  const pushToArrayField = useStore(store, (state) => state.actions.pushToArrayField);
  const component = Children.only(children);
  const length = useStore(store, (state) => (getIn(state.values, name) as Value[]).length);

  const handleClick = useCallback(() => {
    pushToArrayField(name, value === undefined ? '' : value);
  }, [name, pushToArrayField, value]);

  return length < (max ?? Infinity) ? cloneElement(component, { onClick: handleClick }) : null;
};

type FormSpyProps<TValues, TPicked> = {
  on: (values: TValues) => TPicked;
  children: (value: TPicked) => ReactElement | null;
};

export const FormSpy = <TValues, TPicked>({ on, children }: FormSpyProps<TValues, TPicked>) => {
  const { store } = useContext(Context);
  const value = useStore(store, (state) => on(state.values));
  return children(value);
};

export default Form;
